import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAsync, useToast } from '../lib/store';
import type { AdminEggFamily, NpcActivityEntry } from '../lib/types';
import { Button, ErrorState, Panel, Tabs } from '../components/ui';
import { PerformanceTab } from '../components/admin/PerformanceTab';
import { AdminBreedingTab } from '../components/breeding/AdminBreedingTab';
import { AdminEconomyTab } from '../components/economy/AdminEconomyTab';
import { AdminSocialTab } from '../components/economy/AdminSocialTab';
import { AdminWarsTab } from '../components/war/AdminWarsTab';
import { AvatarAdminTab } from '../components/admin/AvatarAdminTab';
import { EggArt } from '../components/EggArt';
import { formatDate, formatNumber, timeAgo } from '../lib/format';

interface AdminPlayer {
  id: string;
  username: string;
  role: string;
  isBanned: boolean;
  isNpc: boolean;
  createdAt: string;
  lastSeenAt: string;
  level: number;
  pokeCoins: number;
  friendshipTokens: number;
  totalHatched: number;
  totalFedOthers: number;
  shinyCount: number;
  pokemonCount: number;
  eggCount: number;
}

interface AdminStats {
  users: number;
  activeToday: number;
  pokemon: number;
  shiny: number;
  shinyRate: number;
  eggsHeld: number;
  eggsHatched: number;
  interactions: number;
  system: {
    species: number;
    npcsEnabled: number;
    npcsTotal: number;
    npcSchedulerOn: boolean;
    npcActivityToday: number;
    guilds: number;
    activeListings: number;
    pendingTrades: number;
  };
  economy: Array<{ reason: string; currency: string; net: number; events: number }>;
}

/**
 * One event, exactly as `GET /admin/events` sends it.
 *
 * This interface used to describe the *pre-framework* payload — `description`,
 * `pointsFor`, `communityGoal`, `communityProgress`, `running`, `eggFamilies` —
 * none of which that endpoint has returned since the Events dashboard took over
 * the listing. Every one of them arrived as `undefined`, and because the
 * community-goal line guarded on `!== null` rather than `!= null`, `undefined`
 * passed the guard and `formatNumber(undefined)` took the whole Admin page down
 * with it the moment any event existed.
 *
 * So the shape is now the one the server actually sends. The retired fields are
 * not re-added: `pointsFor` is superseded by EventObjective and `communityGoal`
 * by EventCommunityGoal, and printing a column the game no longer scores would
 * be a panel that lies quietly rather than loudly.
 */
interface AdminEvent {
  key: string;
  name: string;
  summary: string;
  status: string;
  startsAt: string;
  endsAt: string;
  isActive: boolean;
  visible: boolean;
  participants: number;
  objectives: number;
  tiers: number;
  rewards: number;
}

interface AdminNpc {
  key: string;
  username: string;
  trainerClass: string;
  region: string;
  enabled: boolean;
  activityLevel: number;
  activeHours: [number, number];
  awake: boolean;
  interactionsToday: number;
  activityToday: number;
}

export function AdminPage() {
  const [tab, setTab] = useState<'stats' | 'players' | 'families' | 'events' | 'breeding' | 'economy' | 'social' | 'wars' | 'npcs' | 'avatars' | 'perf' | 'logs'>(
    'stats',
  );

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Admin</h1>
          <div className="page-head__sub">Every action here is server-authoritative and logged.</div>
        </div>
        <Link className="btn" to="/">← Back to game</Link>
      </div>

      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'stats', label: 'Overview' },
          { value: 'players', label: 'Players' },
          { value: 'families', label: 'Egg families' },
          { value: 'events', label: 'Events' },
          { value: 'breeding', label: 'Breeding' },
          { value: 'economy', label: 'Economy' },
          { value: 'social', label: 'Social loop' },
          { value: 'wars', label: 'Guild Wars' },
          { value: 'npcs', label: 'Living Trainers' },
          { value: 'avatars', label: 'Avatars' },
          { value: 'perf', label: 'Performance' },
          { value: 'logs', label: 'Audit log' },
        ]}
      />

      {tab === 'stats' && <StatsTab />}
      {tab === 'players' && <PlayersTab />}
      {tab === 'families' && <FamiliesTab />}
      {tab === 'events' && <EventsTab />}
      {tab === 'breeding' && <AdminBreedingTab />}
      {tab === 'economy' && <AdminEconomyTab />}
      {tab === 'social' && <AdminSocialTab />}
      {tab === 'wars' && <AdminWarsTab />}
      {tab === 'npcs' && <NpcTab />}
      {tab === 'avatars' && <AvatarAdminTab />}
      {tab === 'perf' && <PerformanceTab />}
      {tab === 'logs' && <LogsTab />}
    </div>
  );
}

function StatsTab() {
  const state = useAsync<AdminStats>(() => api.get('/admin/stats'));
  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <div className="pxwell skeleton" style={{ height: 260 }} />;

  const stats = state.data;

  return (
    <div className="stack">
      <Panel title="◈ At a glance">
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
          <Metric label="Trainers" value={formatNumber(stats.users)} />
          <Metric label="Active today" value={formatNumber(stats.activeToday)} />
          <Metric label="Pokémon owned" value={formatNumber(stats.pokemon)} />
          <Metric label="Shiny" value={`${formatNumber(stats.shiny)} (1 in ${stats.shinyRate > 0 ? Math.round(1 / stats.shinyRate) : '—'})`} />
          <Metric label="Eggs held" value={formatNumber(stats.eggsHeld)} />
          <Metric label="Eggs hatched" value={formatNumber(stats.eggsHatched)} />
          <Metric label="Interactions" value={formatNumber(stats.interactions)} />
        </div>
      </Panel>

      {/* "Is the world actually running?" — the numbers you would otherwise
          open a database shell to check. */}
      <Panel title="⚙ System" hint="Trainer counts above exclude Living Trainers">
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
          <Metric label="Species imported" value={formatNumber(stats.system.species)} />
          <Metric
            label="NPC scheduler"
            value={stats.system.npcSchedulerOn ? 'Running' : 'OFF'}
            tone={stats.system.npcSchedulerOn ? 'good' : 'bad'}
          />
          <Metric
            label="Living Trainers"
            value={`${stats.system.npcsEnabled} / ${stats.system.npcsTotal}`}
          />
          <Metric label="NPC actions today" value={formatNumber(stats.system.npcActivityToday)} />
          <Metric label="Guilds" value={formatNumber(stats.system.guilds)} />
          <Metric label="Active listings" value={formatNumber(stats.system.activeListings)} />
          <Metric label="Pending trades" value={formatNumber(stats.system.pendingTrades)} />
        </div>
      </Panel>

      <Panel title="◆ Currency flow" hint="Net movement per source, from the ledger">
        <div className="stack-sm">
          {stats.economy.map((row) => (
            <div key={`${row.reason}-${row.currency}`} className="row row--between tiny">
              <span className="mono">{row.reason}</span>
              <span className="row" style={{ gap: 'var(--gap-sm)' }}>
                <span className="muted">{formatNumber(row.events)} events</span>
                <span
                  className="mono"
                  style={{ color: row.net >= 0 ? 'var(--leaf-dark)' : 'var(--berry-dark)', minWidth: 90, textAlign: 'right' }}
                >
                  {row.net >= 0 ? '+' : ''}
                  {formatNumber(row.net)} {row.currency === 'pokeCoins' ? 'PC' : 'FT'}
                </span>
              </span>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

function PlayersTab() {
  const { push } = useToast();
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [includeNpcs, setIncludeNpcs] = useState(false);
  const state = useAsync<{ players: AdminPlayer[] }>(
    () => api.get('/admin/players', { search: query, ...(includeNpcs ? { includeNpcs: '1' } : {}) }),
    [query, includeNpcs],
  );
  const [selected, setSelected] = useState<AdminPlayer | null>(null);

  async function act(path: string, body: Record<string, unknown>, message: string) {
    try {
      await api.post(path, body);
      push('success', message);
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Failed.');
    }
  }

  return (
    <div className="stack">
      <Panel raised={false}>
        <form
          className="row"
          onSubmit={(event) => {
            event.preventDefault();
            setQuery(search);
          }}
        >
          <input
            className="input"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search username"
          />
          <Button type="submit">Search</Button>
          {/* Off by default. There are 47 Living Trainers against a handful of
              real ones, and this list is capped at 40 rows sorted by last seen
              — with them included, actual players fall off the bottom. */}
          <label className="row tiny" style={{ gap: 6, alignItems: 'center', whiteSpace: 'nowrap' }}>
            <input
              type="checkbox"
              checked={includeNpcs}
              onChange={(event) => setIncludeNpcs(event.target.checked)}
            />
            Include Living Trainers
          </label>
        </form>
      </Panel>

      <Panel title="Players" hint={`${state.data?.players.length ?? 0} shown`}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-xs)' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '3px dashed var(--paper-edge)' }}>
                {['Trainer', 'Lv', 'Coins', 'Tokens', 'Pokémon', 'Hatched', 'Fed', 'Last seen', ''].map((head) => (
                  <th key={head} className="mono" style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {state.data?.players.map((player) => (
                <tr key={player.id} style={{ borderBottom: '1px solid var(--paper-deep)' }}>
                  <td style={{ padding: '6px 8px' }}>
                    <Link to={`/trainer/${player.username}`}>{player.username}</Link>
                    {player.isNpc && (
                      <span className="chip chip--npc chip--sm" style={{ marginLeft: 4 }}>🌿 NPC</span>
                    )}
                    {player.role === 'ADMIN' && <span className="chip" style={{ marginLeft: 4 }}>admin</span>}
                    {player.isBanned && (
                      <span className="chip" style={{ marginLeft: 4, background: 'var(--berry)', color: '#fff' }}>
                        banned
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '6px 8px' }}>{player.level}</td>
                  <td style={{ padding: '6px 8px' }}>{formatNumber(player.pokeCoins)}</td>
                  <td style={{ padding: '6px 8px' }}>{formatNumber(player.friendshipTokens)}</td>
                  <td style={{ padding: '6px 8px' }}>{formatNumber(player.pokemonCount)}</td>
                  <td style={{ padding: '6px 8px' }}>{formatNumber(player.totalHatched)}</td>
                  <td style={{ padding: '6px 8px' }}>{formatNumber(player.totalFedOthers)}</td>
                  <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{timeAgo(player.lastSeenAt)}</td>
                  <td style={{ padding: '6px 8px' }}>
                    <Button size="sm" onClick={() => setSelected(selected?.id === player.id ? null : player)}>
                      {selected?.id === player.id ? 'Close' : 'Manage'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {selected && (
        <Panel title={`Manage ${selected.username}`}>
          <PlayerActions player={selected} onAct={act} />
        </Panel>
      )}

      <BroadcastPanel />
    </div>
  );
}

function PlayerActions({
  player,
  onAct,
}: {
  player: AdminPlayer;
  onAct: (path: string, body: Record<string, unknown>, message: string) => Promise<void>;
}) {
  const families = useAsync<{ families: AdminEggFamily[] }>(() => api.get('/admin/families'));
  const [coins, setCoins] = useState(1000);
  const [tokens, setTokens] = useState(50);
  const [familyKey, setFamilyKey] = useState('');
  const [speciesId, setSpeciesId] = useState(1);
  const [level, setLevel] = useState(5);
  const [shiny, setShiny] = useState(false);

  return (
    <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
      <div className="pxwell stack-sm" style={{ padding: 'var(--gap-sm)' }}>
        <span className="mono tiny">Grant currency</span>
        <div className="row">
          <input className="input" type="number" value={coins} onChange={(e) => setCoins(Number(e.target.value))} />
          <Button size="sm" onClick={() => void onAct('/admin/grant-currency', { userId: player.id, currency: 'pokeCoins', amount: coins }, 'Coins granted.')}>
            PC
          </Button>
        </div>
        <div className="row">
          <input className="input" type="number" value={tokens} onChange={(e) => setTokens(Number(e.target.value))} />
          <Button size="sm" onClick={() => void onAct('/admin/grant-currency', { userId: player.id, currency: 'friendshipTokens', amount: tokens }, 'Tokens granted.')}>
            FT
          </Button>
        </div>
      </div>

      <div className="pxwell stack-sm" style={{ padding: 'var(--gap-sm)' }}>
        <span className="mono tiny">Grant egg</span>
        <select className="select" value={familyKey} onChange={(e) => setFamilyKey(e.target.value)}>
          <option value="">Pick a family…</option>
          {families.data?.families.map((family) => (
            <option key={family.key} value={family.key}>{family.name}</option>
          ))}
        </select>
        <Button
          size="sm"
          disabled={!familyKey}
          onClick={() => void onAct('/admin/grant-egg', { userId: player.id, familyKey, quantity: 1 }, 'Egg granted.')}
        >
          Give egg
        </Button>
      </div>

      <div className="pxwell stack-sm" style={{ padding: 'var(--gap-sm)' }}>
        <span className="mono tiny">Grant Pokémon</span>
        <div className="row">
          <input className="input" type="number" min={1} value={speciesId} onChange={(e) => setSpeciesId(Number(e.target.value))} placeholder="Dex #" />
          <input className="input" type="number" min={1} max={100} value={level} onChange={(e) => setLevel(Number(e.target.value))} placeholder="Level" />
        </div>
        <label className="row tiny">
          <input type="checkbox" checked={shiny} onChange={(e) => setShiny(e.target.checked)} /> Shiny
        </label>
        <Button size="sm" onClick={() => void onAct('/admin/grant-pokemon', { userId: player.id, speciesId, level, isShiny: shiny }, 'Pokémon granted.')}>
          Give Pokémon
        </Button>
      </div>

      <div className="pxwell stack-sm" style={{ padding: 'var(--gap-sm)' }}>
        <span className="mono tiny">Moderation</span>
        <Button
          size="sm"
          variant={player.isBanned ? 'go' : 'primary'}
          onClick={() => void onAct('/admin/set-ban', { userId: player.id, banned: !player.isBanned }, player.isBanned ? 'Unbanned.' : 'Banned.')}
        >
          {player.isBanned ? 'Unban account' : 'Ban account'}
        </Button>
        <span className="tiny muted">Banning also ends all of their active sessions.</span>
      </div>
    </div>
  );
}

function BroadcastPanel() {
  const { push } = useToast();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  async function send() {
    setBusy(true);
    try {
      const result = await api.post<{ recipients: number }>('/admin/broadcast', { title, body });
      push('success', `Sent to ${result.recipients} trainers.`);
      setTitle('');
      setBody('');
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title="◆ Broadcast announcement">
      <div className="stack-sm">
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" maxLength={80} />
        <textarea className="textarea" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Message" maxLength={500} />
        <Button variant="primary" disabled={!title || !body} loading={busy} onClick={() => void send()}>
          Send to every trainer
        </Button>
      </div>
    </Panel>
  );
}

function FamiliesTab() {
  const { push } = useToast();
  const state = useAsync<{ families: AdminEggFamily[] }>(() => api.get('/admin/families'));

  async function update(key: string, changes: Record<string, unknown>) {
    try {
      await api.post('/admin/family', { key, ...changes });
      push('success', 'Family updated.');
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Failed.');
    }
  }

  return (
    <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
      {state.data?.families.map((family) => (
        <Panel
          key={family.key}
          title={family.name}
          hint={family.isActive ? family.theme : 'inactive'}
        >
          <div className="stack-sm">
            <div className="row" style={{ gap: 'var(--gap-sm)' }}>
              <EggArt appearance={family.appearance} size="sm" state="still" label={family.name} />
              <div className="stack-sm tiny muted" style={{ gap: 2 }}>
                <span className="mono">{family.key}</span>
                <span>{family.entryCount} species</span>
                {family.eventKey && <span>event: {family.eventKey}</span>}
                {family.isDailyFree && <span>daily free</span>}
              </div>
            </div>
            <FamilyField
              label={`Price (${family.costCurrency === 'pokeCoins' ? 'PC' : 'FT'})`}
              value={family.price}
              onSave={(value) => void update(family.key, { price: value })}
            />
            <FamilyField
              label="Shiny rate (1 in N)"
              value={family.shinyRate}
              onSave={(value) => void update(family.key, { shinyRate: value })}
            />
            {/* Weight is relative: the rotation picks families in proportion. */}
            <FamilyField
              label="Rotation weight"
              value={family.rotationWeight}
              onSave={(value) => void update(family.key, { rotationWeight: value })}
            />
            <FamilyField
              label="Hatch steps"
              value={family.hatchSteps}
              onSave={(value) => void update(family.key, { hatchSteps: value })}
            />
            <Button size="sm" onClick={() => void update(family.key, { isActive: !family.isActive })}>
              {family.isActive ? 'Deactivate' : 'Reactivate'}
            </Button>
          </div>
        </Panel>
      ))}
    </div>
  );
}

function FamilyField({ label, value, onSave }: { label: string; value: number; onSave: (value: number) => void }) {
  const [draft, setDraft] = useState(value);
  return (
    <div className="row">
      <span className="tiny mono" style={{ flex: 1 }}>{label}</span>
      <input
        className="input"
        type="number"
        value={draft}
        onChange={(event) => setDraft(Number(event.target.value))}
        style={{ width: 90 }}
      />
      <Button size="sm" disabled={draft === value} onClick={() => onSave(draft)}>
        Set
      </Button>
    </div>
  );
}

function LogsTab() {
  const state = useAsync<{
    logs: Array<{ id: string; admin: string; action: string; targetUserId: string | null; meta: unknown; createdAt: string }>;
  }>(() => api.get('/admin/logs'));

  return (
    <Panel title="Audit log">
      <div className="stack-sm">
        {state.data?.logs.map((log) => (
          <div key={log.id} className="pxwell" style={{ padding: 'var(--gap-sm)' }}>
            <div className="row row--between tiny">
              <span className="mono">{log.action}</span>
              <span className="muted">{log.admin} · {timeAgo(log.createdAt)}</span>
            </div>
            <pre className="tiny muted" style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {JSON.stringify(log.meta)}
            </pre>
          </div>
        ))}
        {(state.data?.logs.length ?? 0) === 0 && <p className="tiny muted">No admin actions recorded yet.</p>}
      </div>
    </Panel>
  );
}

/**
 * Events already had a full set of endpoints and no interface at all, so the
 * only way to start or stop a live event was a hand-rolled HTTP request. This
 * is the missing switch.
 *
 * Creating an event is deliberately still an API-only operation: the payload
 * has nested reward tiers and a scoring map, and a half-built form that writes
 * a malformed event into a live game is worse than no form.
 */
function EventsTab() {
  const { push } = useToast();
  const state = useAsync<{ events: AdminEvent[] }>(() => api.get('/admin/events'));
  const [busy, setBusy] = useState<string | null>(null);

  async function setActive(key: string, isActive: boolean) {
    setBusy(key);
    try {
      await api.post('/admin/event/set-active', { key, isActive });
      push('success', isActive ? 'Event switched on.' : 'Event switched off.');
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not update that event.');
    } finally {
      setBusy(null);
    }
  }

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <div className="pxwell skeleton" style={{ height: 240 }} />;

  const events = state.data.events;

  return (
    <Panel title="✦ Events" hint="Switching one off takes effect on the next action scored">
      {events.length === 0 ? (
        <p className="tiny muted">No events defined yet.</p>
      ) : (
        <div className="stack-sm">
          {events.map((event) => (
            <div key={event.key} className="pxwell" style={{ padding: 'var(--gap-sm)' }}>
              <div className="row row--between row--wrap" style={{ gap: 'var(--gap-sm)' }}>
                <div style={{ minWidth: 200, flex: 1 }}>
                  <div className="row" style={{ gap: 6, alignItems: 'center' }}>
                    <strong className="tiny">{event.name}</strong>
                    {/* `isActive` is the admin's switch; `running` also accounts
                        for the date window, which is what players actually see.
                        An event can be switched on and still not be live —
                        either it has not started or it has already finished,
                        and those need different words or the list lies. */}
                    <EventState event={event} />
                  </div>
                  <div className="tiny muted mono">{event.key}</div>
                  <p className="tiny muted" style={{ margin: '4px 0 0' }}>{event.summary}</p>
                </div>

                <div className="tiny muted" style={{ minWidth: 150 }}>
                  <div>{formatDate(event.startsAt)} → {formatDate(event.endsAt)}</div>
                  {/* Counts rather than the retired `pointsFor` map. `?? 0`
                      because this panel is a summary of somebody else's payload
                      and a missing count should read as nothing, never take the
                      page down — which is exactly what happened here. */}
                  <div>
                    {formatNumber(event.participants ?? 0)} participants ·{' '}
                    {event.objectives ?? 0} objectives · {event.tiers ?? 0} tiers ·{' '}
                    {event.rewards ?? 0} rewards
                  </div>
                  {/* The overview switches an event off in an emergency; the
                      authoring happens on its own page, so point at it rather
                      than growing a second editor here. */}
                  <Link className="tiny" to="/admin/events">
                    Open in Events →
                  </Link>
                </div>

                <Button
                  size="sm"
                  variant={event.isActive ? 'default' : 'go'}
                  loading={busy === event.key}
                  onClick={() => void setActive(event.key, !event.isActive)}
                >
                  {event.isActive ? 'Switch off' : 'Switch on'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

/**
 * What state an event is actually in.
 *
 * Derived from `status` and the window rather than from a `running` boolean the
 * server stopped sending. `isActive` is still consulted, because it is a
 * separate kill switch that `status` never replaced — an ACTIVE event with
 * `isActive: false` is switched off whatever its status says, and showing it as
 * live would make the toggle beside it look broken.
 */
function EventState({ event }: { event: AdminEvent }) {
  if (event.isActive === false) return <span className="chip chip--sm">Off</span>;

  if (event.status === 'ACTIVE') {
    const now = Date.now();
    const started = now >= new Date(event.startsAt).getTime();
    const ended = now > new Date(event.endsAt).getTime();
    if (started && !ended) {
      return <span className="chip" style={{ background: 'var(--leaf)', color: '#06210d' }}>LIVE</span>;
    }
    // Flagged ACTIVE but outside its own window. Shown rather than hidden: it
    // is usually something to fix, and `tickEvents` has not yet been round.
    return <span className="chip chip--sm">{ended ? 'Ended · still on' : 'Starting soon'}</span>;
  }

  if (!event.visible) return <span className="chip chip--sm">{event.status} · hidden</span>;
  return <span className="chip chip--sm">{event.status}</span>;
}

/**
 * The Living Trainers run on a timer nobody can see. When Brock stops turning
 * up, the question is always the same — disabled, asleep, out of capacity, or
 * is the scheduler dead? This tab answers it, and lets you run a tick by hand
 * instead of waiting four minutes to find out whether a change worked.
 */
function NpcTab() {
  const { push } = useToast();
  const state = useAsync<{ schedulerEnabled: boolean; npcs: AdminNpc[] }>(() => api.get('/admin/npcs'));
  const log = useAsync<{ activity: NpcActivityEntry[] }>(() => api.get('/admin/npc/activity'));
  const [busy, setBusy] = useState<string | null>(null);
  const [ticking, setTicking] = useState(false);

  async function toggle(key: string, enabled: boolean) {
    setBusy(key);
    try {
      await api.post('/admin/npc/set-enabled', { key, enabled });
      push('success', enabled ? 'Trainer is out in the world again.' : 'Trainer silenced.');
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not update that trainer.');
    } finally {
      setBusy(null);
    }
  }

  async function tick() {
    setTicking(true);
    try {
      const report = await api.post<{ interactions: string[]; ambient: string[]; skipped: number }>(
        '/admin/npc/tick',
        {},
      );
      push(
        'success',
        `${report.interactions.length} interaction(s), ${report.ambient.length} world log line(s).`,
      );
      state.reload();
      log.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Tick failed.');
    } finally {
      setTicking(false);
    }
  }

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <div className="pxwell skeleton" style={{ height: 300 }} />;

  const { schedulerEnabled, npcs } = state.data;

  return (
    <div className="stack">
      <Panel
        title="🌿 Scheduler"
        action={
          <Button size="sm" variant="go" loading={ticking} onClick={() => void tick()}>
            Run one tick
          </Button>
        }
      >
        <div className="row row--wrap" style={{ gap: 'var(--gap)' }}>
          <Metric
            label="Scheduler"
            value={schedulerEnabled ? 'Running' : 'OFF (NPC_ENABLED=false)'}
            tone={schedulerEnabled ? 'good' : 'bad'}
          />
          <Metric label="Enabled" value={`${npcs.filter((n) => n.enabled).length} / ${npcs.length}`} />
          <Metric label="Awake now" value={String(npcs.filter((n) => n.enabled && n.awake).length)} />
          <Metric
            label="Actions today"
            value={formatNumber(npcs.reduce((sum, n) => sum + n.interactionsToday, 0))}
          />
        </div>
        <p className="tiny muted" style={{ marginTop: 'var(--gap-sm)' }}>
          A manual tick obeys every per-player cap, so it cannot be used to spam anyone. Disabling a
          trainer hides them from the scheduler but keeps their past interactions in players'
          history.
        </p>
      </Panel>

      <Panel title="◈ The roster">
        <div className="stack-sm">
          {npcs.map((npc) => (
            <div
              key={npc.key}
              className="row row--between tiny"
              style={{ gap: 'var(--gap-sm)', opacity: npc.enabled ? 1 : 0.5 }}
            >
              <span style={{ minWidth: 150, flex: 1 }}>
                <Link to={`/trainer/${npc.username}`}>{npc.username}</Link>{' '}
                <span className="muted">· {npc.trainerClass}</span>
              </span>
              <span className="muted mono" style={{ minWidth: 90 }}>
                {npc.activeHours[0]}:00–{npc.activeHours[1]}:00 {npc.awake ? '● awake' : '○ asleep'}
              </span>
              <span className="muted mono" style={{ minWidth: 60 }}>lvl {npc.activityLevel}</span>
              <span className="muted mono" style={{ minWidth: 70 }}>{npc.interactionsToday} today</span>
              <Button
                size="sm"
                variant={npc.enabled ? 'default' : 'go'}
                loading={busy === npc.key}
                onClick={() => void toggle(npc.key, !npc.enabled)}
              >
                {npc.enabled ? 'Disable' : 'Enable'}
              </Button>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="❖ Recent world activity">
        {(log.data?.activity ?? []).length === 0 ? (
          <p className="tiny muted">Nothing logged yet.</p>
        ) : (
          <div className="stack-sm">
            {(log.data?.activity ?? []).map((entry) => (
              <div key={entry.id} className="row row--between tiny">
                <span>
                  <span className="chip chip--sm" style={{ marginRight: 6 }}>{entry.kind}</span>
                  {entry.summary}
                </span>
                <span className="muted mono">{timeAgo(entry.createdAt)}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

/** `tone` colours the value for states an admin should notice at a glance. */
function Metric({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'good' | 'bad';
}) {
  const color =
    tone === 'good' ? 'var(--leaf-dark)' : tone === 'bad' ? 'var(--berry-dark)' : undefined;
  return (
    <div className="pxwell" style={{ padding: 'var(--gap-sm)' }}>
      <div className="tiny muted mono" style={{ fontSize: '0.5625rem', letterSpacing: '0.08em' }}>
        {label.toUpperCase()}
      </div>
      <div className="mono" style={{ color }}>{value}</div>
    </div>
  );
}
